import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, Text, useMantineTheme } from "@mantine/core";
import { DataContext } from "../context/DataContext";
import {
  IconCloudUpload,
  IconLicense,
  IconShieldCheck,
  IconShieldLock,
  IconClipboardCheck,
  IconUserCheck,
  IconUserScan,
  IconScan,
  IconHourglassHigh,
  IconSend,
} from "@tabler/icons-react";
import useLocale from "../hooks/useLocale";
import { resolveOnAccentColor } from "../utils/onAccentColor";
import {
  resolveProcessingColors,
  isAnimationOff,
} from "../helpers/component-styles";

// Landscape phones and other short screens.
const SHORT_VIEWPORT = "@media (max-height: 480px)";

const readPrimaryColorFromUrl = () => {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("primaryColor");
  if (!raw) return null;
  return raw.startsWith("#") ? raw : `#${raw}`;
};

// Stage keys + icons live in JS — icons aren't translatable, and the
// key order drives both the stepper dots and the locale message
// lookup. Translated messages come from
// `useLocale("verification-processing")` keyed by type + stage key.
const STAGE_ORDER = {
  document: [
    { key: "uploading", icon: IconCloudUpload },
    { key: "authenticity", icon: IconShieldLock },
    { key: "validity", icon: IconLicense },
    { key: "details", icon: IconClipboardCheck },
    { key: "completing", icon: IconShieldCheck },
  ],
  "document-selfie": [
    { key: "verifying", icon: IconShieldLock },
    { key: "face-to-doc", icon: IconUserScan },
    { key: "liveness", icon: IconScan },
    { key: "completing", icon: IconShieldCheck },
  ],
  // Single-stage variant shown during the doc-selfie's first phase —
  // the document is uploading and we still owe the journey a selfie.
  // Holds on "Uploading your document…" until the parent unmounts it
  // for the face-capture redirect.
  "document-upload": [
    { key: "uploading", icon: IconCloudUpload },
  ],
  // Single-stage variant for the selfie upload phase inside a
  // doc-selfie flow. face-capture/veratad uses this when embedded
  // (i.e. parent supplied onCompletion) so it doesn't bleed into the
  // face-age stages, which only apply to standalone face-age flows.
  "selfie-upload": [
    { key: "uploading", icon: IconCloudUpload },
  ],
  "face-age": [
    { key: "uploading", icon: IconCloudUpload },
    { key: "liveness", icon: IconScan },
    { key: "estimating", icon: IconHourglassHigh },
    { key: "completing", icon: IconShieldCheck },
  ],
  datamatch: [{ key: "verifying", icon: IconUserCheck }],
  generic: [{ key: "verifying", icon: IconShieldCheck }],
  // Shown during the "sending the OTP" phase of phonematch — between
  // the user submitting their phone number and the OTP entry screen
  // appearing. Single-stage, no stepper.
  "sending-otp": [{ key: "sending", icon: IconSend }],
  // Journey API prefill: the publisher declared images it uploads itself
  // and has not confirmed them yet. Single-stage, held until the parent
  // swaps it out (see components/supplied-images-pending.js).
  "receiving-images": [{ key: "receiving", icon: IconHourglassHigh }],
};

// Per-stage display durations (ms) for the non-final stages. The
// final stage is gated by `resultReady` + `minCompletingHold`, not by
// a fixed duration. Scripts under-budget typical backend latency so
// the screen reaches the penultimate stage and waits for the real
// result — script paces the user, backend determines actual hold.
const FLOW_TIMING = {
  document: [2500, 5000, 5000, 4500],
  // doc-selfie verification phase plays through verifying →
  // face-to-doc → liveness → completing once both images are
  // captured and the backend is actually processing.
  "document-selfie": [4500, 4500, 4000],
  "face-age": [1200, 1500, 1800],
  datamatch: [],
  generic: [],
  "sending-otp": [],
  "document-upload": [],
  "selfie-upload": [],
  "receiving-images": [],
};

/**
 * Multi-stage verification processing screen. Visual language mirrors
 * InitialLoadingScreen — branded circle with a pulsing icon over a
 * shimmer-animated message — but the stage (icon + message) advances
 * automatically through a sequence based on the `type` prop.
 *
 * Timing contract: the script auto-advances through every stage
 * including the final "Completing verification…" one. That last
 * stage is the time-killer — it holds indefinitely until the parent
 * flips `resultReady` to true, then waits `minCompletingHold` ms,
 * then fires `onComplete`. Every flow lands on the completing stage
 * regardless of how fast or slow the backend is.
 *
 * Single-stage types (datamatch, generic): the lone stage IS the
 * final stage; the screen waits for `resultReady` then holds
 * `minCompletingHold` before firing `onComplete`. No stepper dots.
 */
const VerificationProcessingScreen = ({
  type = "generic",
  primaryColor,
  resultReady = false,
  onComplete,
  stageDurations,
  minCompletingHold = 1200,
  // Stage keys to leave out of the script, e.g. ["uploading"] when the
  // publisher supplied the images and nothing is uploaded here. The
  // final stage is never dropped.
  skipStages,
}) => {
  const theme = useMantineTheme();
  const locale = useLocale("verification-processing");
  // Publishers can override the button text/icon color via
  // `styles.components.button.color` in the journey session (the
  // same field the rest of the app reads for button label colors —
  // see App.js, handoff, the various form components). The icon
  // rendered inside the accent disc on this screen lives in that
  // same visual role (foreground on the primary surface), so it
  // should honor the same override. With no override we derive the
  // readable foreground from the accent (see utils/onAccentColor)
  // rather than always using white, which disappears on a light
  // primary color.
  // useContext returns undefined safely when this screen is rendered
  // outside a DataContext provider (e.g. some Storybook stories).
  const dataContext = useContext(DataContext);

  const fullOrder = STAGE_ORDER[type] || STAGE_ORDER.generic;
  const baseDurations =
    stageDurations || FLOW_TIMING[type] || FLOW_TIMING.generic;
  // Durations are indexed by stage, so drop a skipped stage's duration
  // with it. Memoized on the key list: Effect A restarts its timer
  // whenever `durations` changes identity.
  const skipKey = Array.isArray(skipStages) ? skipStages.join(",") : "";
  const { order, durations } = useMemo(() => {
    const skip = skipKey ? skipKey.split(",") : [];
    const kept = fullOrder
      .map((stage, i) => ({ stage, i }))
      .filter(({ i, stage }) => i === fullOrder.length - 1 || !skip.includes(stage.key));
    if (kept.length === fullOrder.length) {
      return { order: fullOrder, durations: baseDurations };
    }
    return {
      order: kept.map(({ stage }) => stage),
      durations: kept.map(({ i }) => baseDurations[i]),
    };
  }, [fullOrder, baseDurations, skipKey]);
  const messages = locale?.[type] || locale?.generic || {};
  const stages = order.map((s) => ({
    key: s.key,
    icon: s.icon,
    message: messages[s.key] || s.key,
  }));

  // Resolve the accent so it matches the *button* color rendered
  // elsewhere on the journey, not a hardcoded shade. The theme is
  // configured with `primaryColor` + `primaryShade` (DataContext.js)
  // and `theme.fn.primaryColor()` returns exactly that resolved
  // pairing — same color Mantine uses for `<Button>` backgrounds.
  // Falls back to the legacy explicit lookups when the helper is
  // unavailable (e.g. some Storybook stories mount this without a
  // theme provider).
  const themePrimary =
    (typeof theme.fn?.primaryColor === "function" && theme.fn.primaryColor()) ||
    theme.colors[theme.primaryColor]?.[theme.primaryShade ?? 6] ||
    theme.colors[theme.primaryColor]?.[6];
  const accent =
    primaryColor ||
    readPrimaryColorFromUrl() ||
    themePrimary ||
    theme.colors.blue[6];
  // styles.components.processing: iconColor (disc), ringColor (sonar
  // outline), dotColor (stage stepper), with accentColor as shorthand
  // for all three. Each falls back to `accent` so unconfigured
  // sessions render exactly as before.
  const { iconColor, ringColor, dotColor } = resolveProcessingColors(
    dataContext?.data?.styles?.components?.processing,
    accent,
  );
  // styles.components.processing.stepper === "off" hides the stage dots
  // under the message. Absent or any other value keeps them.
  const showStepper = !isAnimationOff(
    dataContext?.data?.styles?.components?.processing?.stepper,
  );
  // Glyph inside the disc: publisher's button text color, else white or
  // near-black depending on how light the disc actually is (the disc may
  // be iconColor rather than the primary accent).
  const buttonTextColor = resolveOnAccentColor({
    buttonTextColor: dataContext?.data?.styles?.components?.button?.color,
    accent: iconColor,
  });

  const [stageIdx, setStageIdx] = useState(0);
  const finalEnteredAtRef = useRef(null);
  const completeFiredRef = useRef(false);

  const lastIdx = stages.length - 1;

  // Effect A — auto-advance through every stage including the final
  // "Completing verification…" one. The final stage is the
  // time-killer: it holds indefinitely (Effect C gates onComplete on
  // resultReady), so users always land there regardless of whether
  // the backend is faster or slower than the scripted budget.
  useEffect(() => {
    if (stageIdx >= lastIdx) return;
    const duration = durations[stageIdx] ?? 2000;
    const t = setTimeout(() => {
      setStageIdx((prev) => Math.min(prev + 1, lastIdx));
    }, duration);
    return () => clearTimeout(t);
  }, [stageIdx, lastIdx, durations]);

  // Effect B — stamp the moment we enter the final stage so Effect C
  // can compute remaining hold time accurately.
  useEffect(() => {
    if (stageIdx === lastIdx && finalEnteredAtRef.current === null) {
      finalEnteredAtRef.current = Date.now();
    }
  }, [stageIdx, lastIdx]);

  // Effect C — fire onComplete exactly once when (a) we've reached
  // the final stage, (b) the backend result is ready, and (c) the
  // final stage has been visible for at least `minCompletingHold` ms.
  useEffect(() => {
    if (completeFiredRef.current) return;
    if (stageIdx !== lastIdx) return;
    if (!resultReady) return;
    const enteredAt = finalEnteredAtRef.current ?? Date.now();
    const elapsed = Date.now() - enteredAt;
    const remaining = Math.max(0, minCompletingHold - elapsed);
    const t = setTimeout(() => {
      if (completeFiredRef.current) return;
      completeFiredRef.current = true;
      if (onComplete) onComplete();
    }, remaining);
    return () => clearTimeout(t);
  }, [stageIdx, lastIdx, resultReady, minCompletingHold, onComplete]);

  const { icon: CurrentIcon, message } = stages[stageIdx] || stages[0];

  const pageBg =
    theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white;
  const shimmerBase =
    theme.colorScheme === "dark"
      ? theme.colors.gray[4]
      : theme.colors.dark[8];
  const shimmerHighlight =
    theme.colorScheme === "dark" ? theme.white : theme.colors.gray[4];

  return (
    <Box
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: pageBg,
      }}
      // Short viewports (a phone held sideways): the stack is ~340px
      // tall, so centered in the full screen its caption landed under
      // App's absolute footer. Keep the content clear of the footer's
      // 50px strip and compact it. Portrait heights never match.
      // (Padding lives in sx, not style, so the media query can win.)
      sx={{
        padding: 20,
        [SHORT_VIEWPORT]: { paddingBottom: 60, paddingTop: 12 },
      }}
    >
      <Stack
        align="center"
        spacing={24}
        sx={{ [SHORT_VIEWPORT]: { gap: 12 } }}
      >
        {/* Wrapper so the sonar rings can position themselves
            absolutely *around* the icon circle without disrupting the
            Stack layout. Sized larger than the icon so the outermost
            ring still has breathing room from the edge. */}
        <Box
          sx={{
            width: 240,
            height: 240,
            [SHORT_VIEWPORT]: { width: 150, height: 150 },
          }}
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Sonar-style concentric rings — three staggered every
              1.2s create a continuous outward-radiating pulse so the
              screen feels active during long-running verifications
              (e.g. 30s document scans).

              The base transform centers the ring on the icon at rest
              (during the animation-delay before the first iteration).
              animation-fill-mode: both also applies the first keyframe
              during the delay and holds the last keyframe at the end
              of each iteration — belt and suspenders so the staggered
              rings never appear off-center between iterations. */}
          {[0, 1, 2].map((i) => (
            <Box
              key={`ring-${i}`}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: 88,
                height: 88,
                borderRadius: "50%",
                border: `2px solid ${ringColor}`,
                pointerEvents: "none",
                transform: "translate(-50%, -50%)",
                opacity: 0,
                animation: `verificationRingPulse 3.6s ease-out ${i * 1.2}s infinite both`,
                "@keyframes verificationRingPulse": {
                  "0%": {
                    transform: "translate(-50%, -50%) scale(1)",
                    opacity: 0.55,
                  },
                  "100%": {
                    transform: "translate(-50%, -50%) scale(2.7)",
                    opacity: 0,
                  },
                },
              }}
            />
          ))}

          {/* Icon container — `key` remounts on stage change so the
              scale-in bounce replays. Inner Box runs the ongoing pulse
              (opacity + slight scale loop) once the entrance settles. */}
          <Box
            key={`icon-${stageIdx}`}
            sx={{
              position: "relative",
              zIndex: 1,
              width: 88,
              height: 88,
              borderRadius: "50%",
              backgroundColor: iconColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 6px 22px ${iconColor}40`,
              animation:
                "verificationStageIn 600ms cubic-bezier(0.34, 1.56, 0.64, 1) both",
              "@keyframes verificationStageIn": {
                from: { transform: "scale(0.55)", opacity: 0 },
                to: { transform: "scale(1)", opacity: 1 },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                animation:
                  "verificationIconPulse 1.8s ease-in-out infinite 600ms",
                "@keyframes verificationIconPulse": {
                  "0%, 100%": { opacity: 1, transform: "scale(1)" },
                  "50%": { opacity: 0.65, transform: "scale(0.92)" },
                },
              }}
            >
              <CurrentIcon
                size={38}
                stroke={1.6}
                color={buttonTextColor}
              />
            </Box>
          </Box>
        </Box>

        {/* Message — same key-based remount so it fades in fresh with
            each stage. Light-passing-through shimmer continues on a
            loop regardless of stage. */}
        <Text
          key={`text-${stageIdx}`}
          fw={600}
          fz={19}
          ta="center"
          sx={{
            whiteSpace: "nowrap",
            background: `linear-gradient(110deg, ${shimmerBase} 30%, ${shimmerHighlight} 50%, ${shimmerBase} 70%)`,
            backgroundSize: "200% 100%",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
            color: "transparent",
            animation:
              "verificationStageTextIn 500ms ease 100ms both, verificationProcessingShimmer 2.5s linear infinite",
            "@keyframes verificationStageTextIn": {
              from: { opacity: 0, transform: "translateY(4px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
            "@keyframes verificationProcessingShimmer": {
              from: { backgroundPosition: "200% 0" },
              to: { backgroundPosition: "-200% 0" },
            },
          }}
        >
          {message}
        </Text>

        {/* Stage stepper — only meaningful when there are multiple
            stages, so it stays hidden for single-stage flows like
            datamatch. Filled dot = current stage in accent color,
            past stages stay accent-tinted at half opacity, future
            stages are neutral. */}
        {stages.length > 1 && showStepper && (
          <Box
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {stages.map((s, i) => {
              const isCurrent = i === stageIdx;
              const isPast = i < stageIdx;
              return (
                <Box
                  key={`dot-${s.key}`}
                  sx={{
                    width: isCurrent ? 22 : 8,
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: isCurrent
                      ? dotColor
                      : isPast
                        ? `${dotColor}80`
                        : theme.colorScheme === "dark"
                          ? theme.colors.dark[4]
                          : theme.colors.gray[3],
                    transition:
                      "width 350ms ease, background-color 350ms ease",
                  }}
                />
              );
            })}
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default VerificationProcessingScreen;
